'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import { Socket } from 'socket.io-client';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    {
      urls: ['turn:relay1.expressturn.com:3478'],
      username: 'efOXDYNEMPHJKLBZQA',
      credential: 'zxQjJVvHkNHjWfIf',
    },
    {
      urls: 'turns:relay1.expressturn.com:5349',
      username: 'efOXDYNEMPHJKLBZQA',
      credential: 'zxQjJVvHkNHjWfIf',
    },
  ],
};

async function denoiseStream(rawStream: MediaStream) {
  try {
    const audioCtx = new AudioContext({ sampleRate: 48000 });
    const source = audioCtx.createMediaStreamSource(rawStream);
    const destination = audioCtx.createMediaStreamDestination();

    const compressor = audioCtx.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;

    const highPass = audioCtx.createBiquadFilter();
    highPass.type = 'highpass';
    highPass.frequency.value = 100;

    const lowPass = audioCtx.createBiquadFilter();
    lowPass.type = 'lowpass';
    lowPass.frequency.value = 8000;

    source.connect(highPass);
    highPass.connect(lowPass);
    lowPass.connect(compressor);
    compressor.connect(destination);

    const denoisedStream = new MediaStream([
      ...destination.stream.getAudioTracks(),
      ...rawStream.getVideoTracks(),
    ]);

    return { denoisedStream, stopNoise: () => audioCtx.close() };
  } catch (e) {
    console.warn('Denoise failed:', e);
    return { denoisedStream: rawStream, stopNoise: () => {} };
  }
}

async function getLocalStream() {
  try {
    const raw = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
        facingMode: 'user',
      },
      audio: {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
        sampleRate: 48000,
        channelCount: 1,
      },
    });
    const { denoisedStream, stopNoise } = await denoiseStream(raw);
    return { raw, processed: denoisedStream, stopBeauty: stopNoise };
  } catch {
    const canvas = Object.assign(document.createElement('canvas'), { width: 640, height: 480 });
    canvas.getContext('2d')?.fillRect(0, 0, 640, 480);
    const blackVideo = (canvas as any).captureStream(10).getVideoTracks()[0];
    const actx = new AudioContext();
    const silence = actx.createMediaStreamDestination().stream.getAudioTracks()[0];
    const fallback = new MediaStream([blackVideo, silence]);
    return { raw: fallback, processed: fallback, stopBeauty: () => {} };
  }
}

export function useWebRTC(
  socket: Socket,
  isConnected: boolean,
  isInitiator: boolean,
  isMuted: boolean,
  isCameraOff: boolean
) {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const stopNoiseRef = useRef<(() => void) | null>(null);
  const [remoteActive, setRemoteActive] = useState(false);

  const cleanup = useCallback(() => {
    peerRef.current?.close();
    peerRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    stopNoiseRef.current?.();
    stopNoiseRef.current = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    setRemoteActive(false);
  }, []);

  const buildPeer = useCallback(
    (stream: MediaStream) => {
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerRef.current = pc;
      stream.getTracks().forEach((track: MediaStreamTrack) => pc.addTrack(track, stream));
      pc.ontrack = (e) => {
        if (remoteVideoRef.current && e.streams[0]) {
          remoteVideoRef.current.srcObject = e.streams[0];
          setRemoteActive(true);
        }
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && socket) socket.emit('webrtc:ice', { candidate: e.candidate });
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed')
          setRemoteActive(false);
      };
      return pc;
    },
    [socket]
  );

  // Camera hamesha on — socket se independent
  useEffect(() => {
    let cancelled = false;
    async function startCamera() {
      const { raw, processed, stopBeauty } = await getLocalStream();
      if (cancelled) { raw.getTracks().forEach(t => t.stop()); stopBeauty(); return; }
      localStreamRef.current = raw;
      stopNoiseRef.current = stopBeauty;
      if (localVideoRef.current) localVideoRef.current.srcObject = processed;
    }
    if (!localStreamRef.current) startCamera();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isConnected || !socket) {
      peerRef.current?.close();
      peerRef.current = null;
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      setRemoteActive(false);
      return;
    }
    let cancelled = false;

    async function startWebRTC() {
      const { raw, processed, stopBeauty } = await getLocalStream();
      if (cancelled) { raw.getTracks().forEach((t) => t.stop()); stopBeauty(); return; }
      localStreamRef.current = raw;
      stopNoiseRef.current = stopBeauty;
      if (localVideoRef.current) localVideoRef.current.srcObject = processed;
      if (isInitiator) {
        setTimeout(async () => {
          if (cancelled || !socket) return;
          const pc = buildPeer(processed);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit('webrtc:offer', { sdp: offer });
        }, 800);
      }
    }

    startWebRTC();

    socket.on('webrtc:offer', async ({ sdp }: { sdp: RTCSessionDescriptionInit }) => {
      if (cancelled) return;
      const processed = localStreamRef.current || (await getLocalStream()).processed;
      if (localVideoRef.current) localVideoRef.current.srcObject = processed;
      peerRef.current?.close();
      const pc = buildPeer(processed);
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('webrtc:answer', { sdp: answer });
    });

    socket.on('webrtc:answer', async ({ sdp }: { sdp: RTCSessionDescriptionInit }) => {
      if (peerRef.current?.signalingState === 'have-local-offer')
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(sdp));
    });

    socket.on('webrtc:ice', async ({ candidate }: { candidate: RTCIceCandidateInit }) => {
      try {
        if (peerRef.current?.remoteDescription)
          await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn('ICE error', e);
      }
    });

    return () => {
      cancelled = true;
      socket.off('webrtc:offer');
      socket.off('webrtc:answer');
      socket.off('webrtc:ice');
      cleanup();
    };
  }, [isConnected, isInitiator, socket, buildPeer, cleanup]);

  useEffect(() => {
    localStreamRef.current?.getAudioTracks().forEach((t: MediaStreamTrack) => {
      t.enabled = !isMuted;
    });
    localStreamRef.current?.getVideoTracks().forEach((t: MediaStreamTrack) => {
      t.enabled = !isCameraOff;
    });
  }, [isMuted, isCameraOff]);

  return { localVideoRef, remoteVideoRef, remoteActive };
}
